import { ENGINE_API_BASE_URL } from '../config/environment';
import {
  CsrfBootstrapResult,
  DocumentBootstrap,
  DocumentListResult,
  EngineDocument,
  EngineEnvelope,
  EngineErrorEnvelope,
  HandoffExchangeResult,
  SessionRefreshResult,
} from './types';

export class EngineApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly currentEtag?: string,
  ) {
    super(message);
    this.name = 'EngineApiError';
  }
}

let accessToken: string | null = null;
let csrfToken: string | null = null;

export function setEngineAccessToken(token: string | null): void {
  accessToken = token;
}

export function hasEngineAccessToken(): boolean {
  return Boolean(accessToken);
}

export function clearEngineSession(): void {
  accessToken = null;
  csrfToken = null;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  options: { authenticated?: boolean } = {},
): Promise<{ data: T; response: Response }> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (options.authenticated !== false) {
    if (!accessToken) {
      throw new EngineApiError(401, 'ENGINE_AUTH_REQUIRED', '请先连接云作账号');
    }
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  const response = await fetch(`${ENGINE_API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  const text = await response.text();
  let payload: EngineEnvelope<T> | EngineErrorEnvelope | undefined;
  try {
    payload = text ? JSON.parse(text) : undefined;
  } catch {
    payload = undefined;
  }
  if (!response.ok) {
    const error = payload as EngineErrorEnvelope | undefined;
    throw new EngineApiError(
      response.status,
      error?.error?.code || 'ENGINE_HTTP_ERROR',
      error?.error?.message || '云作引擎暂时不可用',
      error?.error?.requestId || response.headers.get('x-request-id') || undefined,
      error?.error?.details?.currentEtag,
    );
  }
  return {
    data: (payload as EngineEnvelope<T>)?.data,
    response,
  };
}

export const engineApiClient = {
  async exchangeHandoff(code: string, codeVerifier: string): Promise<HandoffExchangeResult> {
    const result = await request<HandoffExchangeResult>(
      '/api/v1/auth/handoffs/exchange',
      {
        method: 'POST',
        body: JSON.stringify({ target: 'pm', code, codeVerifier }),
      },
      { authenticated: false },
    );
    setEngineAccessToken(result.data.accessToken);
    csrfToken = result.data.csrfToken;
    return result.data;
  },

  async restoreSession(): Promise<SessionRefreshResult | null> {
    const bootstrap = await request<CsrfBootstrapResult>(
      '/api/v1/engine/sessions/csrf',
      { method: 'GET', cache: 'no-store' },
      { authenticated: false },
    );
    if (!bootstrap.data.available || !bootstrap.data.csrfToken) {
      clearEngineSession();
      return null;
    }
    csrfToken = bootstrap.data.csrfToken;
    return this.refresh();
  },

  async refresh(): Promise<SessionRefreshResult> {
    if (!csrfToken) {
      throw new EngineApiError(401, 'ENGINE_AUTH_REQUIRED', '请先连接云作账号');
    }
    const result = await request<SessionRefreshResult>(
      '/api/v1/engine/sessions/refresh',
      { method: 'POST', headers: { 'X-Engine-CSRF': csrfToken } },
      { authenticated: false },
    );
    setEngineAccessToken(result.data.accessToken);
    csrfToken = result.data.csrfToken;
    return result.data;
  },

  async logout(): Promise<void> {
    if (!csrfToken) {
      clearEngineSession();
      return;
    }
    await request<never>(
      '/api/v1/engine/sessions/logout',
      { method: 'POST', headers: { 'X-Engine-CSRF': csrfToken } },
      { authenticated: false },
    );
    clearEngineSession();
  },

  async listDocuments(cursor?: string): Promise<DocumentListResult> {
    const query = new URLSearchParams({ limit: '50' });
    if (cursor) query.set('cursor', cursor);
    return (await request<DocumentListResult>(`/api/v1/engine/documents?${query}`)).data;
  },

  async createDocument(title: string): Promise<EngineDocument> {
    return (await request<EngineDocument>('/api/v1/engine/documents', {
      method: 'POST',
      headers: { 'Idempotency-Key': createRequestId() },
      body: JSON.stringify({ title }),
    })).data;
  },

  async getDocumentBootstrap(documentId: string): Promise<DocumentBootstrap> {
    return (await request<DocumentBootstrap>(
      `/api/v1/engine/documents/${encodeURIComponent(documentId)}/bootstrap`
    )).data;
  },

  async updateDocumentTitle(documentId: string, title: string, etag: string): Promise<{
    document: EngineDocument;
    etag: string;
  }> {
    const result = await request<EngineDocument>(
      `/api/v1/engine/documents/${encodeURIComponent(documentId)}`,
      {
        method: 'PATCH',
        headers: { 'If-Match': etag },
        body: JSON.stringify({ title }),
      },
    );
    return {
      document: result.data,
      etag: result.response.headers.get('etag') || etag,
    };
  },
};

function createRequestId(): string {
  if (typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
}
